import { action, computed, observable, reaction, makeObservable } from 'mobx';
import React, {ChangeEventHandler} from 'react';
import {SuccessResponse, uploadedFiles} from '../../../../../domain';
import {Disposers, HttpPromiseState, StringControl, toInt} from '../../../../../lib/helpers';
import {Flashcard, FlashcardSide, FlashcardsModel} from '../models';

export class FlashcardSideStore {
  readonly text: StringControl;

  @observable
  imageUrl: string | null;
  @observable.ref
  imageFile: File | null;
  @observable.ref
  imageUpload: HttpPromiseState<SuccessResponse> | null;

  @observable
  audioUrl: string | null;
  @observable.ref
  audioFile: File | null;
  @observable.ref
  audioUpload: HttpPromiseState<SuccessResponse> | null;
  audioElementRef: React.RefObject<HTMLAudioElement>;

  private readonly disposers = new Disposers();

  constructor() {
    makeObservable(this);
    this.text = new StringControl('', '');

    this.imageUrl = null;
    this.imageFile = null;
    this.imageUpload = null;

    this.audioUrl = null;
    this.audioFile = null;
    this.audioUpload = null;
    this.audioElementRef = React.createRef<HTMLAudioElement>();

    this.setup();
  }

  @computed
  get valid(): boolean {
    return this.text.value.trim() !== '' || this.imageUrl !== null;
  }

  @computed
  get model(): FlashcardSide {
    let text: string | null = this.text.value.trim();
    if (text === '')
      text = null;

    return {
      image: this.imageUrl,
      text: text,
      audio: this.audioUrl
    };
  }

  @action
  handleImageFileChange: ChangeEventHandler<HTMLInputElement> =
    (event) => {
      const files = event.target.files;
      if (files === null || files.length === 0)
        this.imageFile = null;
      else
        this.imageFile = files[0];
    };

  @action
  uploadImage = () => {
    if (this.imageFile === null)
      return;
    if (this.imageUpload?.isPending === true)
      return;
    this.imageUpload = uploadedFiles.uploadImage(this.imageFile);
  };

  @action
  clearImage = () => {
    this.imageUrl = null;
  };

  @action
  handleAudioFileChange: ChangeEventHandler<HTMLInputElement> =
    (event) => {
      const files = event.target.files;
      if (files === null || files.length === 0)
        this.audioFile = null;
      else
        this.audioFile = files[0];
    };

  @action
  uploadAudio = () => {
    if (this.audioFile === null)
      return;
    if (this.audioUpload?.isPending === true)
      return;
    this.audioUpload = uploadedFiles.uploadAudio(this.audioFile);
  };

  playAudio = () => {
    if (this.audioElementRef.current === null)
      return;
    this.audioElementRef.current.play();
  };

  @action
  clearAudio = () => {
    this.audioUrl = null;
  };

  private setup() {
    this.disposers.add(
      reaction(
        () => this.imageUpload?.state,
        this.afterImageUploaded),
      reaction(
        () => this.audioUpload?.state,
        this.afterAudioUploaded),
    );
  }

  @action
  private afterImageUploaded = () => {
    if (this.imageUpload?.isResolved !== true)
      return;
    const tmp = this.imageUpload.value.Results.Id;
    const id = toInt(tmp);
    if (id === null)
      return;

    this.imageUrl = uploadedFiles.imageUrl(id);
  };

  @action
  private afterAudioUploaded = () => {
    if (this.audioUpload?.isResolved !== true)
      return;
    const tmp = this.audioUpload.value.Results.Id;
    const id = toInt(tmp);
    if (id === null)
      return;

    this.audioUrl = uploadedFiles.audioUrl(id);
  };

  @action
  sync = (model: FlashcardSide) => {
    this.text.setValue(model.text === null ? '' : model.text);
    this.imageUrl = model.image;
    this.imageFile = null;
    this.imageUpload = null;
    this.audioUrl = model.audio;
    this.audioFile = null;
    this.audioUpload = null;
  };

  dispose() {
    this.disposers.dispose();
  }
}

export class FlashcardStore {
  readonly front = new FlashcardSideStore();
  readonly back = new FlashcardSideStore();

  constructor() {
    makeObservable(this);
  }

  @computed
  get valid() {
    return this.front.valid && this.back.valid;
  }

  @computed
  get model(): Flashcard {
    return {
      front: this.front.model,
      back: this.back.model
    };
  }

  dispose() {
    this.front.dispose();
    this.back.dispose();
  }
}

export class FlashcardsEditStore {
  private readonly disposers = new Disposers();

  @observable.shallow
  cards: FlashcardStore[];

  constructor() {
    makeObservable(this);
    this.cards = [];
    this.addCard();
  }

  sync(model: FlashcardsModel) {
    for (let card of this.cards)
      card.dispose();
    this.cards.length = 0;
    for (let i = 0; i < model.cards.length; i++) {
      let card = model.cards[i];
      this.addCard();
      const store = this.cards[i];
      store.front.sync(card.front);
      store.back.sync(card.back);
    }
  }

  @action
  addCard = () => {
    const card = new FlashcardStore();
    this.cards.push(card);
    this.disposers.add(card);
  };

  @action
  removeCard = (index: number) => {
    const card = this.cards[index];
    card?.dispose();
    this.cards.splice(index, 1);
  };

  @computed
  get valid(): boolean {
    if (this.cards.length === 0)
      return false;
    for (let card of this.cards)
      if (!card.valid)
        return false;
    return true;
  }

  @computed
  get x9Contents(): string {
    if (!this.valid)
      return '';
    const cards: Flashcard[] = [];
    for (let card of this.cards)
      cards.push(card.model);
    const model: FlashcardsModel = {
      cards: cards
    };
    return JSON.stringify(model);
  }

  dispose() {
    this.disposers.dispose();
  }
}
